import * as React from "react";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import Header from "../Includes/Header";
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { getDeviceCommandLogs, unlockDoor } from "../Apihelper/ApiHelperUser";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const ROWS_PER_PAGE = 10;

const ACTION_LABELS = {
  add_or_update_user: "Add / Update User",
  delete_user: "Delete User",
  clear_attendance: "Clear Attendance",
  reboot: "Reboot",
  unlock_door: "Open Door",
  unknown: "Unknown",
};

const STATUS_COLORS = {
  queued: "default",
  dispatched: "info",
  success: "success",
  failed: "error",
};

function DeviceCommandLogsPage() {
  const { colors: appColors } = useAppTheme();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    status: "",
    action: "",
    date_from: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
    date_to: dayjs().format("YYYY-MM-DD"),
    search: "",
  });
  const [expandedGroups, setExpandedGroups] = useState({});
  const [unlocking, setUnlocking] = useState(false);
  const [unlockMessage, setUnlockMessage] = useState("");

  const fetchLogs = useCallback(async () => {
    const stored = localStorage.getItem("userdata");
    if (!stored) {
      navigate("/");
      return;
    }
    const { token } = JSON.parse(stored);
    setLoading(true);
    try {
      const body = {
        status: filters.status || undefined,
        action: filters.action || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        search: filters.search || undefined,
        limit: ROWS_PER_PAGE,
        index: page * ROWS_PER_PAGE,
      };
      const res = await getDeviceCommandLogs(token, body);
      if (res.code === 200 || res.code === "200") {
        setLogs(Array.isArray(res.data) ? res.data : []);
        setTotalCount(res.total_count != null ? res.total_count : 0);
      } else {
        setLogs([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error(err);
      setLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.action, filters.date_from, filters.date_to, filters.search, page, navigate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const groupedLogs = useMemo(() => {
    const groups = new Map();
    logs.forEach((row) => {
      const sn = row.sn || "Unknown device";
      if (!groups.has(sn)) groups.set(sn, []);
      groups.get(sn).push(row);
    });
    return Array.from(groups.entries()).map(([sn, rows]) => ({
      sn,
      rows,
      successCount: rows.filter((r) => r.status === "success").length,
      failedCount: rows.filter((r) => r.status === "failed").length,
    }));
  }, [logs]);

  useEffect(() => {
    // Expand every device group by default whenever the log set changes.
    const next = {};
    groupedLogs.forEach((g) => {
      next[g.sn] = true;
    });
    setExpandedGroups(next);
  }, [groupedLogs]);

  const toggleGroup = (sn) => {
    setExpandedGroups((prev) => ({ ...prev, [sn]: !prev[sn] }));
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchLogs();
  };

  const handleUnlockDoor = async () => {
    const stored = localStorage.getItem("userdata");
    if (!stored) {
      navigate("/");
      return;
    }
    const { token } = JSON.parse(stored);
    setUnlocking(true);
    setUnlockMessage("");
    try {
      const res = await unlockDoor(token);
      if (res.success) {
        setUnlockMessage("Door unlock command sent.");
        fetchLogs();
      } else {
        setUnlockMessage(res.message || "Failed to queue door unlock.");
      }
    } catch (err) {
      console.error(err);
      setUnlockMessage("Failed to queue door unlock.");
    } finally {
      setUnlocking(false);
      setTimeout(() => setUnlockMessage(""), 5000);
    }
  };

  const formatDate = (str) => {
    if (!str) return "—";
    try {
      return dayjs(str).format("DD/MM/YYYY HH:mm:ss");
    } catch {
      return str;
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <CssBaseline />
      <Header />
      <Box component="main" sx={{ flexGrow: 1, overflowY: "auto", p: 2 }}>
        <Box sx={{ mt: { xs: "72px", sm: "80px" } }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
            <Typography variant="h5" sx={{ color: appColors.evergreen, fontWeight: 600 }}>
              Device Command Logs
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {unlockMessage && (
                <Typography variant="body2" sx={{ color: appColors.evergreen }}>
                  {unlockMessage}
                </Typography>
              )}
              <Button
                variant="contained"
                startIcon={<LockOpenIcon />}
                onClick={handleUnlockDoor}
                disabled={unlocking}
                sx={{
                  bgcolor: "#B45309",
                  "&:hover": { bgcolor: "#92400E" },
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {unlocking ? "Opening..." : "Open Door"}
              </Button>
            </Box>
          </Box>

          <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                  >
                    <MenuItem value="">All statuses</MenuItem>
                    <MenuItem value="queued">Queued</MenuItem>
                    <MenuItem value="dispatched">Dispatched</MenuItem>
                    <MenuItem value="success">Success</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={filters.action}
                    label="Action"
                    onChange={(e) => handleFilterChange("action", e.target.value)}
                  >
                    <MenuItem value="">All actions</MenuItem>
                    <MenuItem value="add_or_update_user">Add / Update User</MenuItem>
                    <MenuItem value="delete_user">Delete User</MenuItem>
                    <MenuItem value="clear_attendance">Clear Attendance</MenuItem>
                    <MenuItem value="reboot">Reboot</MenuItem>
                    <MenuItem value="unlock_door">Open Door</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange("date_from", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange("date_to", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  placeholder="PIN, card, SN..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleApplyFilters}
                  sx={{
                    bgcolor: appColors.evergreen,
                    "&:hover": { bgcolor: appColors.pine },
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Apply
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {loading ? (
            <Paper
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 6,
                borderRadius: 2,
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
              }}
            >
              <CircularProgress sx={{ color: appColors.evergreen }} />
            </Paper>
          ) : groupedLogs.length === 0 ? (
            <Paper
              sx={{
                py: 4,
                textAlign: "center",
                borderRadius: 2,
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
              }}
            >
              No device command logs found.
            </Paper>
          ) : (
            <>
              {groupedLogs.map((group) => (
                <Accordion
                  key={group.sn}
                  expanded={!!expandedGroups[group.sn]}
                  onChange={() => toggleGroup(group.sn)}
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
                    "&:before": { display: "none" },
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />} sx={{ bgcolor: appColors.evergreen }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <Typography sx={{ color: "#fff", fontWeight: 600 }}>
                        Device SN: {group.sn}
                      </Typography>
                      <Chip
                        label={`${group.rows.length} request${group.rows.length === 1 ? "" : "s"}`}
                        size="small"
                        sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "#fff" }}
                      />
                      {group.successCount > 0 && (
                        <Chip label={`${group.successCount} success`} size="small" color="success" />
                      )}
                      {group.failedCount > 0 && (
                        <Chip label={`${group.failedCount} failed`} size="small" color="error" />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size="small" aria-label={`device command logs for ${group.sn}`}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: "#f0f2f1" }}>
                            <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>PIN (Member/User ID)</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Card Number</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Reason / Error</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Queued At</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Acked At</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.rows.map((row) => (
                            <TableRow
                              key={row.id}
                              sx={{
                                "&:nth-of-type(odd)": { backgroundColor: "#f9fafb" },
                                "&:hover": { backgroundColor: "#eff6ff" },
                              }}
                            >
                              <TableCell>{ACTION_LABELS[row.action] || row.action || "—"}</TableCell>
                              <TableCell>{row.pin || "—"}</TableCell>
                              <TableCell>{row.card_number || "—"}</TableCell>
                              <TableCell>
                                <Chip
                                  label={row.status}
                                  size="small"
                                  color={STATUS_COLORS[row.status] || "default"}
                                  sx={{ textTransform: "capitalize" }}
                                />
                              </TableCell>
                              <TableCell sx={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {row.error_message || (row.status === "success" ? "Completed successfully" : "—")}
                              </TableCell>
                              <TableCell>{formatDate(row.created_at)}</TableCell>
                              <TableCell>{formatDate(row.acked_at)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
              <Paper sx={{ borderRadius: 2, boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)" }}>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handlePageChange}
                  rowsPerPage={ROWS_PER_PAGE}
                  rowsPerPageOptions={[ROWS_PER_PAGE]}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
                  }
                  sx={{
                    '& .MuiTablePagination-toolbar': {
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      gap: 1,
                      py: 1,
                    },
                  }}
                />
              </Paper>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default DeviceCommandLogsPage;
